import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `http://localhost:4000${url}`;
};

const STATUS = {
  pending:          { label: 'Pending',          color: 'bg-amber-100 text-amber-700'     },
  pending_payment:  { label: 'Awaiting Payment', color: 'bg-orange-100 text-orange-700'   },
  confirmed:        { label: 'Confirmed',        color: 'bg-blue-100 text-blue-700'       },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-purple-100 text-purple-700'   },
  completed:        { label: 'Delivered',        color: 'bg-emerald-100 text-emerald-700' },
  cancelled:        { label: 'Cancelled',        color: 'bg-red-100 text-red-600'         },
};

const STEPS = [
  { key: 'pending',          label: 'Order Placed'     },
  { key: 'confirmed',        label: 'Seller Accepted'  },
  { key: 'out_for_delivery', label: 'Out for Delivery' },
  { key: 'completed',        label: 'Delivered'        },
];

const stepIndex = (status) =>
  ({ pending: 0, pending_payment: 0, confirmed: 1, out_for_delivery: 2, completed: 3 }[status] ?? 0);

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function usePagination(data, perPage) {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) pages.push(i);
    else if (pages[pages.length - 1] !== '...') pages.push('...');
  }
  return (
    <div className="flex items-center justify-between mt-3">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`d-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:bg-surface-container disabled:opacity-30 transition-all">
        Next<span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

// ── Single Order Card ──────────────────────────────────────
function OrderCard({ order, onPayNow, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS[order.status] || STATUS.pending;
  const currentStep = stepIndex(order.status);
  const isCancelled = order.status === 'cancelled';
  const [showDispute, setShowDispute] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputing, setDisputing] = useState(false);

  return (
    <>
      {showDispute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm mx-4 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-red-600 text-[24px]">report</span>
            </div>
            <h3 className="font-black text-on-surface text-lg">Raise a Dispute</h3>
            <p className="text-xs text-secondary">Order #{order.id} — {order.animal_name}</p>
            <textarea rows={3} value={disputeReason}
              onChange={e => setDisputeReason(e.target.value)}
              placeholder="Describe the issue with this payment..."
              className="w-full border border-outline-variant/30 rounded-xl p-3 text-sm
                        outline-none focus:ring-2 focus:ring-red-400 resize-none" />
            <div className="flex gap-3">
              <button onClick={() => { setShowDispute(false); setDisputeReason(''); }}
                className="flex-1 py-2.5 rounded-xl border border-outline-variant/30
                          text-sm font-semibold text-secondary hover:bg-surface-container transition-all">
                Cancel
              </button>
              <button disabled={disputing || !disputeReason.trim()}
                onClick={async () => {
                  setDisputing(true);
                  try {
                    await api.post('/api/payment/dispute', {
                      order_id: order.id,
                      order_type: 'animal',
                      reason: disputeReason,
                    });
                    setShowDispute(false);
                    setDisputeReason('');
                    alert('Dispute submitted. Admin will review shortly.');
                  } catch (e) {
                    alert(e.response?.data?.error || 'Failed to raise dispute');
                  } finally { setDisputing(false); }
                }}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold
                          hover:bg-red-700 transition-all disabled:opacity-50">
                {disputing ? 'Submitting…' : 'Submit Dispute'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 hover:border-primary/20 transition-all cursor-pointer"
     onClick={() => setExpanded(!expanded)}>

      {/* ── Top row ── */}
      <div className="flex items-start gap-3 p-3 sm:p-4">
        {order.image_url
          ? <img src={getImageUrl(order.image_url)} alt={order.animal_name}
                 className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-cover flex-shrink-0" />
          : <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-surface-container flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-2xl text-outline-variant">pets</span>
            </div>
        }

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-on-surface">{order.animal_name || '—'}</p>
            {order.category && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {order.category}
              </span>
            )}
          </div>
          <p className="text-[11px] text-secondary font-mono mt-0.5">
            Order #{order.id} · {new Date(order.created_at).toLocaleDateString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric'
            })} · {new Date(order.created_at).toLocaleTimeString('en-IN', {
              hour: '2-digit', minute: '2-digit'
            })}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="material-symbols-outlined text-[13px] text-secondary">
              {order.payment_method === 'online' ? 'credit_card' : 'payments'}
            </span>
            <span className="text-[11px] text-secondary">
              {order.payment_method === 'online' ? 'Online Payment' : 'Cash on Delivery'}
            </span>
            {order.payment_status === 'paid' && (
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                Paid ✓
              </span>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          <p className="font-black text-primary text-base sm:text-lg">
            ₹{Number(order.amount || 0).toLocaleString('en-IN')}
          </p>
          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${st.color}`}>
            {st.label}
          </span>
        </div>

        <button onClick={(e) => e.stopPropagation()}
          className="ml-2 p-2 rounded-xl hover:bg-surface-container text-secondary transition-all flex-shrink-0">
          <span className="material-symbols-outlined text-[20px]">
            {expanded ? 'expand_less' : 'expand_more'}
          </span>
        </button>
      </div>

      {/* ── Pay Now banner ── */}
      {order.status === 'pending_payment' && (
        <div className="mx-3 mb-3 px-3 py-3 bg-orange-50 border border-orange-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-orange-500 text-[20px]">payment</span>
            <div>
              <p className="text-sm font-bold text-orange-800">Payment Pending</p>
              <p className="text-[11px] text-orange-600">Complete payment to confirm your order</p>
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onPayNow(order); }}
            className="px-4 py-2 rounded-xl bg-orange-500 text-white font-bold text-sm hover:bg-orange-600 transition-all flex-shrink-0">
            Pay Now
          </button>
        </div>
      )}

      {/* ── OTP banner ── */}
      {order.status === 'out_for_delivery' && order.delivery_otp && (
        <div className="mx-3 mb-3 px-3 py-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-purple-600 text-[22px]">pin</span>
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-purple-600">Your Delivery OTP</p>
              <p className="text-2xl sm:text-3xl font-black text-purple-800 tracking-[0.2em] sm:tracking-[0.3em] leading-tight">{order.delivery_otp}</p>
              <p className="text-[11px] text-purple-600 mt-0.5">Show this to the delivery person</p>
            </div>
          </div>
          <span className="material-symbols-outlined text-purple-200 text-[48px] hidden sm:block">local_shipping</span>
        </div>
      )}

      {/* ── Expanded detail ── */}
      {expanded && (
        <div className="border-t border-outline-variant/10 p-4 space-y-4 bg-surface-container-low/40">

          {/* Order & Seller Info */}
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-outline-variant/10">
            <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-3 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[14px]">receipt_long</span>
              Order Info
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">

              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">
                  {order._type === 'meat' ? 'Product' : 'Animal'}
                </p>
                <p className="text-sm font-bold text-on-surface">
                  {order.animal_name || order.product_name || '—'}
                </p>
                {order.category && <p className="text-[11px] text-secondary">{order.category}</p>}
                {order.breed && <p className="text-[11px] text-secondary">{order.breed}</p>}
              </div>

              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Seller</p>
                <p className="text-sm font-bold text-on-surface">{order.seller_name || '—'}</p>
                {order.seller_phone && (
                  <a href={`tel:${order.seller_phone}`}
                    className="text-[11px] text-primary hover:underline flex items-center gap-1">
                    <span className="material-symbols-outlined text-[12px]">call</span>
                    {order.seller_phone}
                  </a>
                )}
              </div>

              {order.quantity > 1 && (
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Qty</p>
                  <p className="text-sm font-bold text-on-surface">× {order.quantity}</p>
                </div>
              )}

              {order.weight_kg && (
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Weight</p>
                  <p className="text-sm font-bold text-on-surface">
                    {order.weight_kg} {order.unit || 'kg'}
                  </p>
                </div>
              )}

              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Placed On</p>
                <p className="text-sm font-bold text-on-surface">
                  {new Date(order.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric'
                  })}
                </p>
                <p className="text-[11px] text-secondary">
                  {new Date(order.created_at).toLocaleTimeString('en-IN', {
                    hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              </div>

              {order.updated_at && order.updated_at !== order.created_at && (
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest text-secondary mb-0.5">Last Updated</p>
                  <p className="text-sm font-bold text-on-surface">
                    {new Date(order.updated_at).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })}
                  </p>
                  <p className="text-[11px] text-secondary">
                    {new Date(order.updated_at).toLocaleTimeString('en-IN', {
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              )}

            </div>
          </div>
          
          {/* Journey tracker */}
          {!isCancelled && (
            <div className="bg-white rounded-xl p-4 border border-outline-variant/10">
              <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-5">Order Journey</p>
              <div className="flex items-start justify-between relative">
                <div className="absolute left-4 right-4 top-4 h-0.5 bg-surface-container" />
                <div className="absolute left-4 top-4 h-0.5 bg-primary transition-all duration-700"
                     style={{ width: `calc(${(currentStep / 3) * 100}% - 2rem)` }} />
                {STEPS.map((step, i) => (
                  <div key={step.key} className="flex flex-col items-center gap-2 z-10 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                      i < currentStep  ? 'bg-primary border-primary text-white'
                      : i === currentStep ? 'bg-primary border-primary text-white ring-4 ring-primary/20'
                      : 'bg-white border-outline-variant/30 text-outline-variant'
                    }`}>
                      {i < currentStep
                        ? <span className="material-symbols-outlined text-[16px]">check</span>
                        : <span className="text-xs font-black">{i + 1}</span>
                      }
                    </div>
                    <p className={`text-[9px] sm:text-[10px] font-bold text-center max-w-[48px] sm:max-w-[64px] leading-tight ${
                      i <= currentStep ? 'text-primary' : 'text-outline-variant'
                    }`}>{step.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Delivery details */}
            <div className="bg-white rounded-xl p-4 border border-outline-variant/10">
              <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-3 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">local_shipping</span>
                Delivery Details
              </p>
              {order.delivery_address
                ? <div className="flex gap-2 text-sm text-secondary">
                    <span className="material-symbols-outlined text-[16px] mt-0.5 flex-shrink-0">home</span>
                    <span>{order.delivery_address}</span>
                  </div>
                : <p className="text-outline italic text-xs">No delivery address provided</p>
              }
              {order.note && (
                <div className="pt-2 border-t border-outline-variant/10 mt-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-secondary mb-1">Note</p>
                  <p className="text-xs italic">"{order.note}"</p>
                </div>
              )}
            </div>

            {/* Delivery person */}
            {order.delivery_boy_name ? (
              <div className="bg-white rounded-xl p-4 border border-outline-variant/10">
                <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-3 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">delivery_dining</span>
                  Delivery Person
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-black">
                    {order.delivery_boy_name[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-bold">{order.delivery_boy_name}</p>
                    <a href={`tel:${order.delivery_boy_phone}`}
                       className="text-[11px] text-primary hover:underline flex items-center gap-1">
                      <span className="material-symbols-outlined text-[13px]">call</span>
                      {order.delivery_boy_phone}
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-4 border border-outline-variant/10 flex items-center gap-3">
                <span className="material-symbols-outlined text-outline-variant text-[28px]">delivery_dining</span>
                <div>
                  <p className="text-sm font-semibold text-secondary">Delivery not assigned yet</p>
                  <p className="text-[11px] text-outline">Seller will assign a delivery person soon</p>
                </div>
              </div>
            )}

            {/* Payment summary */}
            <div className="bg-white rounded-xl p-4 border border-outline-variant/10 sm:col-span-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-secondary mb-3 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[14px]">receipt</span>
                Payment Summary
              </p>
              <div className="flex justify-between items-center">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-secondary">
                    <span className="material-symbols-outlined text-[16px]">
                      {order.payment_method === 'online' ? 'credit_card' : 'payments'}
                    </span>
                    {order.payment_method === 'online' ? 'Paid online via Razorpay' : 'Cash on Delivery'}
                  </div>
                  {order.razorpay_payment_id && (
                    <p className="text-[10px] font-mono text-outline">Txn: {order.razorpay_payment_id}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-primary">
                    ₹{Number(order.amount || 0).toLocaleString('en-IN')}
                  </p>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    order.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {order.payment_status === 'paid' ? '✓ Paid' : 'Unpaid'}
                  </span>
                </div>
              </div>
            </div>
            {['pending', 'confirmed'].includes(order.status) && (
              <button onClick={async (e) => {
                e.stopPropagation();
                if (!window.confirm('Cancel this order?')) return;
                try {
                  await api.patch(`/api/butcher/orders/${order.id}/cancel`);
                  onRefresh();
                } catch (err) {
                  alert(err.response?.data?.error || 'Failed to cancel');
                }
              }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                    border border-red-200 text-red-600 text-xs font-bold
                    hover:bg-red-50 transition-all sm:col-span-2">
                <span className="material-symbols-outlined text-[16px]">cancel</span>
                Cancel Order
              </button>
            )}
             {order.status === 'completed' && order.payment_method === 'online' && order.payment_status === 'paid' && (
              <button onClick={e => { e.stopPropagation(); setShowDispute(true); }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                           border border-red-200 text-red-600 text-xs font-bold
                           hover:bg-red-50 transition-all sm:col-span-2">
                <span className="material-symbols-outlined text-[16px]">report</span>
                Raise Payment Dispute
              </button>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// ── Main Export ────────────────────────────────────────────
export default function OrdersPanel() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    api.get('/api/butcher/orders')
      .then(r => setOrders(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handlePayNow = async (order) => {
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { alert('Razorpay failed to load.'); return; }

      const { data } = await api.post('/api/payment/create-order',
        { order_id: order.id },
      );

      new window.Razorpay({
        key:         data.key_id,
        amount:      data.amount,
        currency:    data.currency,
        name:        'Elite Goats',
        description: `Order #${order.id} — ${order.animal_name}`,
        order_id:    data.razorpay_order_id,
        prefill:     { name: user?.name || '', contact: user?.phone || '' },
        theme:       { color: '#1a6b3c' },
        handler: async (response) => {
          await api.post('/api/payment/verify', {
            razorpay_order_id:   response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature:  response.razorpay_signature,
            order_id:            order.id,
          }, { headers: AUTH() });
          fetchOrders();
        },
      }).open();
    } catch (err) {
      alert(err.response?.data?.error || 'Payment failed');
    }
  };

  const [filter, setFilter] = useState('all');

  const counts = {
    total:  orders.length,
    active: orders.filter(o => ['pending', 'confirmed'].includes(o.status)).length,
    onWay:  orders.filter(o => o.status === 'out_for_delivery').length,
    done:   orders.filter(o => o.status === 'completed').length,
  };

  const filtered =
    filter === 'active' ? orders.filter(o => ['pending', 'confirmed'].includes(o.status))
    : filter === 'onWay'  ? orders.filter(o => o.status === 'out_for_delivery')
    : filter === 'done'   ? orders.filter(o => o.status === 'completed')
    : orders;

  const pagination = usePagination(filtered, 5);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-black text-on-surface">My Orders</h2>
        <p className="text-sm text-secondary mt-1">Track all your animal purchases.</p>
      </div>

      {orders.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {[
            { key: 'all',    label: 'Total',      val: counts.total,  color: 'bg-white border border-outline-variant/20 text-secondary' },
            { key: 'active', label: 'Active',     val: counts.active, color: 'bg-amber-50 text-amber-700'    },
            { key: 'onWay',  label: 'On the Way', val: counts.onWay,  color: 'bg-purple-50 text-purple-700'  },
            { key: 'done',   label: 'Delivered',  val: counts.done,   color: 'bg-emerald-50 text-emerald-700' },
            ].map(p => (
              <button key={p.label} onClick={() => setFilter(p.key)}
                className={`px-3 py-1.5 rounded-xl text-xs sm:text-sm font-bold shadow-sm transition-all
                  ${filter === p.key ? 'ring-2 ring-offset-1 ring-primary scale-105' : ''} ${p.color}`}>
              {p.val} {p.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center shadow-sm border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-outline-variant">receipt_long</span>
          <p className="mt-3 font-bold text-on-surface">No orders yet</p>
          <p className="text-xs text-secondary mt-1">Browse animals and place your first order.</p>
        </div>
           ) : (
        <>
          <div className="space-y-3">
            {pagination.slice.map(order => (
              <OrderCard key={order.id} order={order} onPayNow={handlePayNow} onRefresh={fetchOrders} />
            ))}
          </div>

          {filtered.length > 5 && (
            <div className="bg-white rounded-2xl px-5 py-4 shadow-sm border border-outline-variant/10">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-secondary">
                  {((pagination.page - 1) * 5) + 1}–{Math.min(pagination.page * 5, filtered.length)} of {filtered.length} orders
                </p>
              </div>
              <PaginationBar
                page={pagination.page}
                totalPages={pagination.totalPages}
                setPage={pagination.setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}